import { isPlatformBrowser } from "@angular/common";
import { computed, inject, Injectable, PLATFORM_ID, signal } from "@angular/core";

import { SignLanguageService } from "./sign-language.service";
import { environment } from "@azm/environments/environment";

import type {
	AccessibilityContrastMode,
	AccessibilityFontScale,
	AccessibilityPreferences,
} from "@azm/core/interfaces/accessibility-preferences";

const STORAGE_KEY = "azm-a11y-preferences";
const FONT_SCALE_STEPS: AccessibilityFontScale[] = [100, 112.5, 125, 150];
const CONTRAST_MODES: AccessibilityContrastMode[] = ["default", "high", "grayscale"];
const DEFAULT_PREFERENCES: AccessibilityPreferences = { fontScale: 100, contrastMode: "default" };

export type AccessibilityToolAction = "increase-text" | "decrease-text" | "contrast" | "sign-language" | "reset";

@Injectable({
	providedIn: "root",
})
export class AccessibilityService {
	readonly #platformId = inject(PLATFORM_ID);
	readonly #signLanguage = inject(SignLanguageService);

	readonly enabled = environment.enableAccessibilityTools === true;

	readonly fontScale = signal<AccessibilityFontScale>(DEFAULT_PREFERENCES.fontScale);
	readonly contrastMode = signal<AccessibilityContrastMode>(DEFAULT_PREFERENCES.contrastMode);
	readonly signLanguageEnabled = this.#signLanguage.enabled;
	readonly signLanguageAvailable = this.#signLanguage.isAvailable;
	readonly liveMessage = signal("");
	readonly liveMessageParams = signal<Record<string, string | number>>({});
	readonly isTextEnlarged = computed(() => this.fontScale() !== DEFAULT_PREFERENCES.fontScale);
	readonly canIncreaseText = computed(() => this.#fontScaleIndex() < FONT_SCALE_STEPS.length - 1);
	readonly canDecreaseText = computed(() => this.#fontScaleIndex() > 0);
	readonly isContrastActive = computed(() => this.contrastMode() !== "default");
	readonly hasActivePreferences = computed(() => this.isTextEnlarged() || this.isContrastActive() || this.signLanguageEnabled());

	#initialized = false;

	contrastMenuLabelKey(mode: AccessibilityContrastMode = this.contrastMode()): string {
		switch (mode) {
			case "high":
				return "home_external.footer.accessibility.contrast_mode_high";
			case "grayscale":
				return "home_external.footer.accessibility.contrast_mode_grayscale";
			default:
				return "home_external.footer.accessibility.contrast";
		}
	}

	initialize(): void {
		if (this.#initialized || !isPlatformBrowser(this.#platformId)) {
			return;
		}

		this.#initialized = true;

		if (!this.enabled) {
			try {
				localStorage.removeItem(STORAGE_KEY);
			} catch {}

			this.#applyPreferences(DEFAULT_PREFERENCES);
			return;
		}

		this.#applyPreferences(this.#readPreferences());
	}

	runAction(action: AccessibilityToolAction): void {
		if (!this.enabled || !isPlatformBrowser(this.#platformId) || this.isToolActionDisabled(action)) {
			return;
		}

		switch (action) {
			case "increase-text":
				this.#stepTextSize(1);
				break;
			case "decrease-text":
				this.#stepTextSize(-1);
				break;
			case "contrast":
				this.#cycleContrast();
				break;
			case "sign-language":
				this.#toggleSignLanguage();
				break;
			case "reset":
				this.#resetPreferences();
				break;
		}
	}

	isToolActionDisabled(action: AccessibilityToolAction): boolean {
		switch (action) {
			case "increase-text":
				return !this.canIncreaseText();
			case "decrease-text":
				return !this.canDecreaseText();
			default:
				return false;
		}
	}

	#fontScaleIndex(): number {
		const index = FONT_SCALE_STEPS.indexOf(this.fontScale());
		return index >= 0 ? index : 0;
	}

	#stepTextSize(step: 1 | -1): void {
		const currentIndex = this.#fontScaleIndex();
		const lastIndex = FONT_SCALE_STEPS.length - 1;
		const nextIndex = Math.min(lastIndex, Math.max(0, currentIndex + step));

		if (nextIndex === currentIndex) {
			this.#announce(
				step === 1
					? "home_external.footer.accessibility.announce_increase_max"
					: "home_external.footer.accessibility.announce_increase_default",
			);
			return;
		}

		const nextScale = FONT_SCALE_STEPS[nextIndex] ?? DEFAULT_PREFERENCES.fontScale;
		this.fontScale.set(nextScale);
		this.#persistAndApply();

		if (nextScale === DEFAULT_PREFERENCES.fontScale) {
			this.#announce("home_external.footer.accessibility.announce_increase_default");
			return;
		}

		this.#announce("home_external.footer.accessibility.announce_increase", { percent: nextScale });
	}

	#toggleSignLanguage(): void {
		if (!this.#signLanguage.isAvailable()) {
			return;
		}

		this.#signLanguage.toggle();

		if (this.#signLanguage.loadFailed()) {
			this.#announce("home_external.footer.accessibility.announce_sign_language_failed");
			return;
		}

		this.#announce(
			this.#signLanguage.enabled()
				? "home_external.footer.accessibility.announce_sign_language_on"
				: "home_external.footer.accessibility.announce_sign_language_off",
		);
	}

	#cycleContrast(): void {
		const currentIndex = Math.max(0, CONTRAST_MODES.indexOf(this.contrastMode()));
		const nextMode = CONTRAST_MODES[(currentIndex + 1) % CONTRAST_MODES.length] ?? "default";

		this.contrastMode.set(nextMode);
		this.#persistAndApply();
		this.#announce(`home_external.footer.accessibility.announce_contrast_${nextMode}`);
	}

	#resetPreferences(): void {
		this.fontScale.set(DEFAULT_PREFERENCES.fontScale);
		this.contrastMode.set(DEFAULT_PREFERENCES.contrastMode);
		this.#signLanguage.disable();
		this.#persistAndApply();
		this.#announce("home_external.footer.accessibility.announce_reset");
	}

	#persistAndApply(): void {
		const preferences: AccessibilityPreferences = {
			fontScale: this.fontScale(),
			contrastMode: this.contrastMode(),
		};

		try {
			const isDefault =
				preferences.fontScale === DEFAULT_PREFERENCES.fontScale && preferences.contrastMode === DEFAULT_PREFERENCES.contrastMode;

			if (isDefault) {
				localStorage.removeItem(STORAGE_KEY);
			} else {
				localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
			}
		} catch {}

		this.#applyPreferences(preferences);
	}

	#applyPreferences(preferences: AccessibilityPreferences): void {
		const root = document.documentElement;

		this.fontScale.set(preferences.fontScale);
		this.contrastMode.set(preferences.contrastMode);

		root.style.setProperty("--azm-a11y-font-scale", String(preferences.fontScale / 100));
		root.setAttribute("data-azm-font-scale", String(preferences.fontScale));
		root.removeAttribute("data-azm-a11y-highlight-links");

		if (preferences.contrastMode === "default") {
			root.removeAttribute("data-azm-a11y-contrast");
		} else {
			root.setAttribute("data-azm-a11y-contrast", preferences.contrastMode);
		}
	}

	#readPreferences(): AccessibilityPreferences {
		try {
			const raw = localStorage.getItem(STORAGE_KEY);
			if (!raw) {
				return DEFAULT_PREFERENCES;
			}

			const parsed = JSON.parse(raw) as Partial<AccessibilityPreferences & { highlightLinks?: boolean }>;
			const fontScale = FONT_SCALE_STEPS.includes(parsed.fontScale as AccessibilityFontScale)
				? (parsed.fontScale as AccessibilityFontScale)
				: DEFAULT_PREFERENCES.fontScale;

			const contrastMode = CONTRAST_MODES.includes(parsed.contrastMode as AccessibilityContrastMode)
				? (parsed.contrastMode as AccessibilityContrastMode)
				: DEFAULT_PREFERENCES.contrastMode;

			return {
				fontScale,
				contrastMode,
			};
		} catch {
			return DEFAULT_PREFERENCES;
		}
	}

	#announce(messageKey: string, params: Record<string, string | number> = {}): void {
		this.liveMessage.set("");
		this.liveMessageParams.set({});
		queueMicrotask(() => {
			this.liveMessageParams.set(params);
			this.liveMessage.set(messageKey);
		});
	}
}
